<script>
import {ElMessage, ElNotification} from "element-plus";
import {DocumentCopy, Download, Filter, InfoFilled, Refresh} from "@element-plus/icons-vue";
import * as echarts from "echarts/core";
import {BarChart} from "echarts/charts";
import {GridComponent, TooltipComponent} from "echarts/components";
import {CanvasRenderer} from "echarts/renderers";
import MfEmpty from "@/components/core/MfEmpty.vue";

echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer]);

const RETENTION_DAYS = 30;

const ROLE_SUGGESTIONS = [
  {value: "admin", label: "Admin"},
  {value: "moderator", label: "Moderator"},
  {value: "region_manager", label: "Region manager"},
  {value: "deputy_manager", label: "Deputat menejeri"},
  {value: "ministry_manager", label: "Vazirlik menejeri"},
  {value: "monitoring", label: "Monitoring"},
  {value: "my_garden_moderator", label: "Mening bog'im moderatori"},
];

const METHOD_OPTIONS = ["POST", "PUT", "PATCH", "DELETE"];

const STATUS_CODE_SUGGESTIONS = [200, 201, 400, 401, 403, 404, 409, 422, 500];

const BAR_COLOR = "rgba(0, 119, 145, 0.75)";
const BAR_COLOR_SELECTED = "rgb(66, 78, 121)";

export default {
  name: "ActionLogs",
  components: {MfEmpty, DocumentCopy, Download, Filter, InfoFilled, Refresh},
  data() {
    return {
      // histogram (always fixed to the last 30 days, independent of table filters)
      histogramLoading: false,
      histogramError: false,
      buckets: [],
      histogramDateFrom: "",
      histogramDateTo: "",
      chartInstance: null,

      // table
      loading: false,
      hasError: false,
      errorMessage: "",
      items: [],
      count: 0,
      page: 1,
      limit: 50,

      // routes dropdown
      routesOptions: [],

      // filters
      dateRange: ["", ""],
      activePreset: "today",
      onlyErrors: false,
      slowOnly: false,
      userRole: "",
      method: "",
      route: "",
      userId: "",
      ip: "",
      statusCode: null,
      showMoreFilters: false,

      exporting: false,

      // user detail modal
      userDialogVisible: false,
      userLoading: false,
      userError: "",
      userDetail: null,

      roleSuggestions: ROLE_SUGGESTIONS,
      methodOptions: METHOD_OPTIONS,
      statusCodeSuggestions: STATUS_CODE_SUGGESTIONS,
    };
  },
  computed: {
    dateFrom() {
      return this.dateRange?.[0] || "";
    },
    dateTo() {
      return this.dateRange?.[1] || "";
    },
    minAllowedDate() {
      return this.$moment().subtract(RETENTION_DAYS - 1, "days").startOf("day").toDate();
    },
    selectedBucketDate() {
      return this.dateFrom && this.dateFrom === this.dateTo ? this.dateFrom : null;
    },
    chartCategories() {
      if (!this.histogramDateFrom || !this.histogramDateTo) return [];
      const days = [];
      let cursor = this.$moment(this.histogramDateFrom);
      const end = this.$moment(this.histogramDateTo);
      while (cursor.isSameOrBefore(end, "day")) {
        days.push(cursor.format("YYYY-MM-DD"));
        cursor = cursor.add(1, "day");
      }
      return days;
    },
    advancedFilterCount() {
      return [this.userId, this.ip, this.statusCode, this.slowOnly].filter(Boolean).length;
    },
    isDefaultTodayEmpty() {
      return (
          !this.items.length &&
          this.activePreset === "today" &&
          !this.onlyErrors &&
          !this.userRole &&
          !this.method &&
          !this.route &&
          !this.advancedFilterCount
      );
    },
    hasActiveFilters() {
      return (
          this.onlyErrors ||
          !!this.userRole ||
          !!this.method ||
          !!this.route ||
          !!this.advancedFilterCount
      );
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.count / this.limit));
    },
  },
  watch: {
    selectedBucketDate() {
      this.renderChart();
    },
  },
  methods: {
    // ---- date range helpers ----
    disabledDate(date) {
      return date < this.minAllowedDate || date > new Date();
    },
    onDateRangeChange() {
      if (!this.dateRange || !this.dateRange[0] || !this.dateRange[1]) {
        this.dateRange = [this.$moment().format("YYYY-MM-DD"), this.$moment().format("YYYY-MM-DD")];
      }
      this.activePreset = null;
      this.onFilterChange();
    },
    setPreset(preset) {
      const today = this.$moment();
      if (preset === "today") {
        this.dateRange = [today.format("YYYY-MM-DD"), today.format("YYYY-MM-DD")];
      } else if (preset === "7d") {
        this.dateRange = [
          today.clone().subtract(6, "days").format("YYYY-MM-DD"),
          today.format("YYYY-MM-DD"),
        ];
      } else if (preset === "30d") {
        this.dateRange = [
          today.clone().subtract(RETENTION_DAYS - 1, "days").format("YYYY-MM-DD"),
          today.format("YYYY-MM-DD"),
        ];
      }
      this.activePreset = preset;
      this.onFilterChange();
    },

    // ---- filters ----
    onOnlyErrorsChange(val) {
      if (val) this.statusCode = null;
      this.onFilterChange();
    },
    onStatusCodeChange(val) {
      if (val) this.onlyErrors = false;
      this.onFilterChange();
    },
    onFilterChange() {
      this.page = 1;
      this.fetchTable();
    },
    resetAdvancedFilters() {
      this.userId = "";
      this.ip = "";
      this.statusCode = null;
      this.slowOnly = false;
      this.onFilterChange();
    },
    resetAllFilters() {
      this.onlyErrors = false;
      this.userRole = "";
      this.method = "";
      this.route = "";
      this.userId = "";
      this.ip = "";
      this.statusCode = null;
      this.slowOnly = false;
      this.onFilterChange();
    },

    // ---- API params ----
    buildFilterParams() {
      const params = {date_from: this.dateFrom, date_to: this.dateTo};
      if (this.onlyErrors) params.min_status_code = 400;
      if (this.userRole) params.user_role = this.userRole;
      if (this.method) params.method = this.method;
      if (this.route) params.route = this.route;
      if (this.userId) params.user_id = this.userId;
      if (this.ip) params.ip = this.ip;
      if (this.statusCode) params.status_code = this.statusCode;
      if (this.slowOnly) params.min_latency_ms = 1000;
      return params;
    },

    // ---- error handling (dispatch on `type`, not HTTP status) ----
    async parseErrorBody(error) {
      const resp = error?.response;
      const raw = resp?.data;
      if (raw instanceof Blob) {
        try {
          return JSON.parse(await raw.text());
        } catch (e) {
          return {message: "Kutilmagan xatolik yuz berdi."};
        }
      }
      return raw || {message: "Kutilmagan xatolik yuz berdi."};
    },
    describeError(body) {
      const message = body?.message || "Kutilmagan xatolik yuz berdi.";
      switch (body?.type) {
        case "AUTHORIZATION":
          return {title: "Sessiya tugagan", message: "Iltimos, qayta tizimga kiring."};
        case "FORBIDDEN":
          return {title: "Ruxsat yo'q", message: "Bu bo'lim faqat admin uchun."};
        case "BADREQUEST":
          return {title: "Noto'g'ri so'rov", message};
        default:
          return {title: "Xatolik", message};
      }
    },

    // ---- table ----
    fetchTable() {
      this.loading = true;
      this.hasError = false;
      this.errorMessage = "";
      this.$http
          .get("api/v1/admin/action-logs", {
            params: {...this.buildFilterParams(), limit: this.limit, offset: this.limit * (this.page - 1)},
          })
          .then(({data}) => {
            this.items = data?.items || [];
            this.count = data?.count || 0;
          })
          .catch(async (error) => {
            const body = await this.parseErrorBody(error);
            const {title, message} = this.describeError(body);
            this.hasError = true;
            this.errorMessage = message;
            ElNotification.error({title, message});
          })
          .finally(() => {
            this.loading = false;
          });
    },
    // ---- user detail modal ----
    openUserDetail(userId) {
      if (!userId) return;
      this.userDialogVisible = true;
      this.userLoading = true;
      this.userError = "";
      this.userDetail = null;
      this.$http
          .get(`api/v1/users/${userId}`, {params: {cache: "off"}})
          .then(({data}) => {
            this.userDetail = data;
          })
          .catch(async (error) => {
            const body = await this.parseErrorBody(error);
            this.userError = body?.message || "Foydalanuvchini yuklab bo'lmadi.";
          })
          .finally(() => {
            this.userLoading = false;
          });
    },
    formatGender(gender) {
      if (gender === "M") return "Erkak";
      if (gender === "F") return "Ayol";
      return gender || "-";
    },
    userInitials(name) {
      if (!name) return "?";
      return name
          .trim()
          .split(/\s+/)
          .slice(0, 2)
          .map((w) => w[0])
          .join("")
          .toUpperCase();
    },
    async copyText(text) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(String(text));
        ElMessage.success("Nusxa olindi");
      } catch (e) {
        ElMessage.warning("Nusxa olib bo'lmadi");
      }
    },
    handleSizeChange() {
      this.page = 1;
      this.fetchTable();
    },
    handleCurrentChange() {
      this.fetchTable();
    },
    toggleExpand(row) {
      this.$refs.logsTable?.toggleRowExpansion(row);
    },
    formatDate(value) {
      return value ? this.$moment(value).format("DD.MM.YYYY HH:mm:ss") : "-";
    },
    statusTagType(code) {
      if (code >= 500) return "danger";
      if (code >= 400) return "warning";
      return "success";
    },
    responseEmptyText(statusCode) {
      return statusCode < 400 ? "Muvaffaqiyatli javoblar yozilmaydi" : "Bo'sh";
    },
    prettyBody(raw) {
      if (!raw) return "";
      try {
        return JSON.stringify(JSON.parse(raw), null, 2);
      } catch (e) {
        return raw;
      }
    },

    // ---- routes dropdown ----
    fetchRoutes() {
      this.$http
          .get("api/v1/admin/action-logs/routes")
          .then(({data}) => {
            this.routesOptions = data?.routes || [];
          })
          .catch(() => {
            // Non-critical — the route filter just stays empty; other filters still work.
          });
    },

    // ---- histogram ----
    fetchHistogram() {
      const today = this.$moment();
      this.histogramDateFrom = today.clone().subtract(RETENTION_DAYS - 1, "days").format("YYYY-MM-DD");
      this.histogramDateTo = today.format("YYYY-MM-DD");
      this.histogramLoading = true;
      this.histogramError = false;
      this.$http
          .get("api/v1/admin/action-logs/histogram", {
            params: {date_from: this.histogramDateFrom, date_to: this.histogramDateTo},
          })
          .then(({data}) => {
            this.buckets = data?.buckets || [];
          })
          .catch(() => {
            this.histogramError = true;
          })
          .finally(() => {
            this.histogramLoading = false;
            this.$nextTick(() => this.renderChart());
          });
    },
    renderChart() {
      if (!this.$refs.histogramChart) return;
      if (!this.chartInstance) {
        this.chartInstance = echarts.init(this.$refs.histogramChart);
        this.chartInstance.on("click", (params) => {
          this.dateRange = [params.name, params.name];
          this.activePreset = null;
          this.onFilterChange();
        });
      }
      const countByDate = {};
      this.buckets.forEach((b) => {
        countByDate[b.date] = b.count;
      });
      const selected = this.selectedBucketDate;
      const categories = this.chartCategories;
      const data = categories.map((date) => ({
        value: countByDate[date] || 0,
        itemStyle: {
          color: date === selected ? BAR_COLOR_SELECTED : BAR_COLOR,
          borderRadius: [4, 4, 0, 0],
        },
      }));
      this.chartInstance.setOption({
        grid: {left: 40, right: 12, top: 16, bottom: 28},
        tooltip: {
          trigger: "item",
          formatter: (p) =>
              `<b>${p.value}</b> ta amal<br/>${this.$moment(p.name).format("DD.MM.YYYY")}`,
        },
        xAxis: {
          type: "category",
          data: categories,
          axisLabel: {
            formatter: (v) => this.$moment(v).format("DD.MM"),
            color: "#999",
            fontSize: 11,
          },
          axisLine: {lineStyle: {color: "#e4e7ed"}},
          axisTick: {show: false},
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          splitLine: {lineStyle: {color: "#f0f2f7"}},
          axisLabel: {color: "#999", fontSize: 11},
        },
        series: [
          {
            type: "bar",
            data,
            barMaxWidth: 24,
            emphasis: {itemStyle: {color: BAR_COLOR_SELECTED}},
          },
        ],
      });
    },
    handleResize() {
      this.chartInstance?.resize();
    },

    // ---- excel export ----
    saveBlob(res) {
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `action-logs_${this.dateFrom}_${this.dateTo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    async exportExcel() {
      if (this.exporting || !this.dateFrom || !this.dateTo) return;
      this.exporting = true;
      try {
        const res = await this.$http.get("api/v1/admin/action-logs/excel", {
          params: this.buildFilterParams(),
          responseType: "blob",
        });
        this.saveBlob(res);
        ElMessage.success("Excel fayl yuklab olindi");
      } catch (error) {
        const body = await this.parseErrorBody(error);
        if (body?.type === "BADREQUEST") {
          // Expected business-rule rejection (too many rows, bad range) — not a technical failure.
          ElMessage.warning(
              body.message || "Natija juda ko'p, sana oralig'ini yoki boshqa filtrlarni toraytiring."
          );
        } else {
          const {title, message} = this.describeError(body);
          ElNotification.error({title, message});
        }
      } finally {
        this.exporting = false;
      }
    },
  },
  mounted() {
    this.dateRange = [this.$moment().format("YYYY-MM-DD"), this.$moment().format("YYYY-MM-DD")];
    this.fetchRoutes();
    this.fetchHistogram();
    this.fetchTable();
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    this.chartInstance?.dispose();
  },
};
</script>

<template>
  <div class="action-logs">
    <div class="header">
      <div>
        <h2 class="page-title">Amallar jurnali</h2>
        <p class="page-subtitle">Foydalanuvchilarning o'zgartiruvchi amallari (audit log)</p>
      </div>
      <el-button
          type="primary"
          :loading="exporting"
          :disabled="!dateFrom || !dateTo"
          @click="exportExcel"
      >
        <el-icon v-if="!exporting" style="margin-right: 4px"><Download/></el-icon>
        Excel'ga yuklab olish
      </el-button>
    </div>

    <div class="retention-note">
      <el-icon><InfoFilled/></el-icon>
      <span>Loglar faqat oxirgi 30 kun saqlanadi, undan eski yozuvlar avtomatik o'chiriladi.</span>
    </div>

    <div class="histogram-card">
      <el-skeleton v-if="histogramLoading" :rows="3" animated/>
      <el-result
          v-else-if="histogramError"
          icon="warning"
          title="Gistogrammani yuklab bo'lmadi"
          class="histogram-error"
      >
        <template #extra>
          <el-button size="small" @click="fetchHistogram">
            <el-icon style="margin-right: 4px"><Refresh/></el-icon>
            Qayta urinish
          </el-button>
        </template>
      </el-result>
      <div v-show="!histogramLoading && !histogramError" ref="histogramChart" class="histogram-chart"/>
    </div>

    <div class="filters-panel">
      <div class="filters-row filters-row--primary">
        <div class="date-filter">
          <el-date-picker
              v-model="dateRange"
              type="daterange"
              format="DD.MM.YYYY"
              value-format="YYYY-MM-DD"
              range-separator="—"
              start-placeholder="Boshlanish"
              end-placeholder="Tugash"
              :disabled-date="disabledDate"
              :clearable="false"
              style="width: 260px"
              @change="onDateRangeChange"
          />
          <el-radio-group v-model="activePreset" class="preset-group">
            <el-radio-button label="today" @click="setPreset('today')">Bugun</el-radio-button>
            <el-radio-button label="7d" @click="setPreset('7d')">Oxirgi 7 kun</el-radio-button>
            <el-radio-button label="30d" @click="setPreset('30d')">Oxirgi 30 kun</el-radio-button>
          </el-radio-group>
        </div>

        <label class="toggle-filter toggle-filter--danger">
          <el-switch v-model="onlyErrors" active-color="#f56c6c" @change="onOnlyErrorsChange"/>
          Faqat xatolar
        </label>
      </div>

      <div class="filters-row">
        <el-select
            v-model="userRole"
            placeholder="Rol"
            clearable
            filterable
            allow-create
            default-first-option
            style="width: 200px"
            @change="onFilterChange"
        >
          <el-option v-for="r in roleSuggestions" :key="r.value" :label="r.label" :value="r.value"/>
        </el-select>

        <el-select
            v-model="method"
            placeholder="Metod"
            clearable
            style="width: 160px"
            @change="onFilterChange"
        >
          <el-option v-for="m in methodOptions" :key="m" :label="m" :value="m"/>
        </el-select>

        <el-select
            v-model="route"
            placeholder="Yo'l (route)"
            clearable
            filterable
            style="width: 260px"
            @change="onFilterChange"
        >
          <el-option v-for="r in routesOptions" :key="r" :label="r" :value="r"/>
        </el-select>

        <el-tooltip :content="showMoreFilters ? 'Kamroq filtr' : 'Ko\'proq filtr'" placement="top">
          <el-button
              circle
              :type="showMoreFilters ? 'primary' : 'default'"
              class="more-filters-btn"
              @click="showMoreFilters = !showMoreFilters"
          >
            <el-icon><Filter/></el-icon>
            <span v-if="advancedFilterCount" class="more-filters-badge">{{ advancedFilterCount }}</span>
          </el-button>
        </el-tooltip>
      </div>

      <div v-if="showMoreFilters" class="filters-row filters-row--advanced">
        <el-input
            v-model="userId"
            placeholder="user_id (UUID)"
            clearable
            style="width: 220px"
            @keyup.enter="onFilterChange"
            @clear="onFilterChange"
        />
        <el-input
            v-model="ip"
            placeholder="IP manzil"
            clearable
            style="width: 180px"
            @keyup.enter="onFilterChange"
            @clear="onFilterChange"
        />
        <el-select
            v-model="statusCode"
            placeholder="Aniq status kod"
            clearable
            filterable
            allow-create
            :disabled="onlyErrors"
            style="width: 180px"
            @change="onStatusCodeChange"
        >
          <el-option v-for="c in statusCodeSuggestions" :key="c" :label="c" :value="c"/>
        </el-select>
        <label class="toggle-filter toggle-filter--warning">
          <el-switch v-model="slowOnly" active-color="#e6a23c" @change="onFilterChange"/>
          Sekin so'rovlar (&gt;1s)
        </label>
        <el-button size="small" @click="resetAdvancedFilters">Tozalash</el-button>
      </div>
    </div>

    <div v-if="hasError" class="state-block">
      <el-result icon="error" title="Loglarni yuklab bo'lmadi" :sub-title="errorMessage">
        <template #extra>
          <el-button type="primary" @click="fetchTable">
            <el-icon style="margin-right: 4px"><Refresh/></el-icon>
            Qayta urinish
          </el-button>
        </template>
      </el-result>
    </div>

    <div v-else-if="loading" class="state-block">
      <el-skeleton :rows="8" animated/>
    </div>

    <div v-else-if="!items.length" class="state-block">
      <MfEmpty
          :title="isDefaultTodayEmpty ? 'Bugun hech qanday amal qayd etilmagan' : 'Bu davrda amallar topilmadi'"
          description="Sana oralig'ini kengaytiring yoki filtrlarni o'zgartiring"
      />
      <div class="empty-actions">
        <el-button v-if="!hasActiveFilters" type="primary" @click="setPreset('7d')">
          Oxirgi 7 kun
        </el-button>
        <el-button v-else @click="resetAllFilters">Filtrlarni tozalash</el-button>
      </div>
    </div>

    <template v-else>
      <el-table ref="logsTable" :data="items" class="table" border @row-click="toggleExpand">
        <el-table-column type="expand">
          <template #default="props">
            <div class="detail-panel">
              <div class="detail-panel__meta">
                <span><b>IP:</b> {{ props.row.ip || "-" }}</span>
                <span><b>User-Agent:</b> {{ props.row.user_agent || "-" }}</span>
              </div>
              <div class="detail-panel__bodies">
                <div class="detail-panel__body">
                  <h5>So'rov tanasi</h5>
                  <pre v-if="props.row.request_body">{{ prettyBody(props.row.request_body) }}</pre>
                  <p v-else class="detail-panel__empty">Bo'sh</p>
                </div>
                <div class="detail-panel__body">
                  <h5>Javob tanasi</h5>
                  <pre v-if="props.row.response_body">{{ prettyBody(props.row.response_body) }}</pre>
                  <p v-else class="detail-panel__empty">
                    {{ responseEmptyText(props.row.status_code) }}
                  </p>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Vaqt" width="170">
          <template #default="scope">{{ formatDate(scope.row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="Foydalanuvchi" min-width="140">
          <template #default="scope">
            <span
                v-if="scope.row.user_id"
                class="mono user-link"
                :title="scope.row.user_id"
                @click.stop="openUserDetail(scope.row.user_id)"
            >{{ scope.row.user_id.slice(0, 8) }}</span>
            <span v-else class="mono">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="user_role" label="Rol" width="140">
          <template #default="scope">
            <el-tag size="small" type="info" effect="plain">{{ scope.row.user_role || "-" }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="method" label="Metod" width="90"/>
        <el-table-column label="Yo'l" min-width="220">
          <template #default="scope">
            <div class="mono">{{ scope.row.path }}</div>
            <div class="route-hint">{{ scope.row.route }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Status" width="90" align="center">
          <template #default="scope">
            <el-tag size="small" :type="statusTagType(scope.row.status_code)">
              {{ scope.row.status_code }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Latency" width="100" align="right">
          <template #default="scope">
            <span :class="{'latency--slow': scope.row.latency_ms >= 1000}">
              {{ scope.row.latency_ms }} ms
            </span>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
          background
          layout="sizes, prev, pager, next, total"
          :total="count"
          :page-sizes="[20, 50, 100, 200]"
          v-model:page-size="limit"
          v-model:current-page="page"
          class="pagination"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
      />
    </template>

    <el-dialog
        v-model="userDialogVisible"
        width="720px"
        top="6vh"
        destroy-on-close
        class="user-dialog"
    >
      <template #header>
        <span class="user-dialog__title">Foydalanuvchi ma'lumotlari</span>
      </template>

      <div v-if="userLoading" class="user-dialog__state">
        <el-skeleton :rows="6" animated/>
      </div>
      <div v-else-if="userError" class="user-dialog__state">
        <el-result icon="error" title="Xatolik" :sub-title="userError"/>
      </div>
      <template v-else-if="userDetail">
        <div class="user-hero">
          <div class="user-hero__avatar">{{ userInitials(userDetail.fullname) }}</div>
          <div class="user-hero__info">
            <div class="user-hero__name">{{ userDetail.fullname || "—" }}</div>
            <div class="user-hero__tags">
              <el-tag size="small" type="primary" effect="light">{{ userDetail.role || "—" }}</el-tag>
              <el-tag size="small" :type="userDetail.is_verified ? 'success' : 'info'" effect="light">
                {{ userDetail.is_verified ? "Tasdiqlangan" : "Tasdiqlanmagan" }}
              </el-tag>
              <el-tag v-if="userDetail.gender" size="small" type="info" effect="plain">
                {{ formatGender(userDetail.gender) }}
              </el-tag>
            </div>
          </div>
        </div>

        <div class="user-section">
          <div class="user-section__title">Shaxsiy</div>
          <dl class="info-grid">
            <div class="info-grid__item">
              <dt>Tug'ilgan sana</dt>
              <dd>{{ userDetail.birth_date || "-" }}</dd>
            </div>
            <div class="info-grid__item">
              <dt>Kasbi</dt>
              <dd>{{ userDetail.profession || "-" }}</dd>
            </div>
            <div class="info-grid__item">
              <dt>Passport</dt>
              <dd>{{ userDetail.passport_number || "-" }}</dd>
            </div>
            <div class="info-grid__item">
              <dt>PINFL</dt>
              <dd>{{ userDetail.pinfl || "-" }}</dd>
            </div>
          </dl>
        </div>

        <div class="user-section">
          <div class="user-section__title">Aloqa</div>
          <dl class="info-grid">
            <div class="info-grid__item">
              <dt>Login</dt>
              <dd>{{ userDetail.username || "-" }}</dd>
            </div>
            <div class="info-grid__item">
              <dt>Telefon</dt>
              <dd>{{ userDetail.phone_number || "-" }}</dd>
            </div>
            <div class="info-grid__item info-grid__item--full">
              <dt>Qo'shimcha telefon</dt>
              <dd>
                <template v-if="userDetail.extra_phone_numbers && userDetail.extra_phone_numbers.length">
                  <el-tag
                      v-for="p in userDetail.extra_phone_numbers"
                      :key="p"
                      size="small"
                      effect="plain"
                      class="phone-chip"
                  >{{ p }}</el-tag>
                </template>
                <span v-else>-</span>
              </dd>
            </div>
          </dl>
        </div>

        <div class="user-section">
          <div class="user-section__title">Manzil</div>
          <dl class="info-grid">
            <div class="info-grid__item">
              <dt>Viloyat</dt>
              <dd>
                {{ userDetail.region_title || "-" }}
                <span class="dim" v-if="userDetail.region_id">#{{ userDetail.region_id }}</span>
              </dd>
            </div>
            <div class="info-grid__item">
              <dt>Tuman</dt>
              <dd>
                {{ userDetail.district_title || "-" }}
                <span class="dim" v-if="userDetail.district_id">#{{ userDetail.district_id }}</span>
              </dd>
            </div>
            <div class="info-grid__item info-grid__item--full">
              <dt>Mahalla</dt>
              <dd>
                {{ userDetail.quarter_title || "-" }}
                <span class="dim" v-if="userDetail.quarter_id">#{{ userDetail.quarter_id }}</span>
              </dd>
            </div>
          </dl>
        </div>

        <div class="user-section">
          <div class="user-section__title">Tizim</div>
          <dl class="info-grid">
            <div class="info-grid__item">
              <dt>Bildirishnomalar</dt>
              <dd>{{ userDetail.notifications_count ?? 0 }}</dd>
            </div>
            <div class="info-grid__item">
              <dt>Rasm ID</dt>
              <dd>{{ userDetail.image_id || "-" }}</dd>
            </div>
            <div class="info-grid__item">
              <dt>Oxirgi kirish</dt>
              <dd>{{ userDetail.last_login_date ? formatDate(userDetail.last_login_date) : "-" }}</dd>
            </div>
            <div class="info-grid__item">
              <dt>Holat tarixi</dt>
              <dd>{{ (userDetail.status_history && userDetail.status_history.length) || 0 }} ta</dd>
            </div>
            <div class="info-grid__item">
              <dt>Yaratilgan</dt>
              <dd>{{ formatDate(userDetail.created_at) }}</dd>
            </div>
            <div class="info-grid__item">
              <dt>Yangilangan</dt>
              <dd>{{ formatDate(userDetail.updated_at) }}</dd>
            </div>
            <div class="info-grid__item info-grid__item--full">
              <dt>ID</dt>
              <dd class="id-cell">
                <span class="mono">{{ userDetail.id }}</span>
                <el-button
                    text
                    size="small"
                    class="copy-btn"
                    title="Nusxa olish"
                    @click="copyText(userDetail.id)"
                >
                  <el-icon><DocumentCopy/></el-icon>
                </el-button>
              </dd>
            </div>
          </dl>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;

  .page-title {
    margin: 0;
    font-family: "Montserrat-Bold", sans-serif;
    color: #0a194e;
  }

  .page-subtitle {
    margin: 4px 0 0;
    color: #777777;
    font-size: 13px;
  }
}

.retention-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 16px;
  background: #f5f7fb;
  border-radius: 6px;
  font-size: 12px;
  color: #424e79;

  .el-icon {
    color: #424e79;
    flex-shrink: 0;
  }
}

.histogram-card {
  margin-bottom: 20px;
  padding: 12px 16px;
  border: 1px solid rgba(66, 78, 121, 0.08);
  border-radius: 10px;
}

.histogram-error {
  padding: 12px 0;
}

.histogram-chart {
  width: 100%;
  height: 200px;
}

.filters-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #fafbfd;
  border: 1px solid rgba(66, 78, 121, 0.08);
  border-radius: 10px;
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;

  &--primary {
    justify-content: space-between;
  }

  &--advanced {
    padding-top: 10px;
    border-top: 1px dashed rgba(66, 78, 121, 0.15);
  }
}

.date-filter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.preset-group {
  :deep(.el-radio-button__inner) {
    padding: 8px 12px;
  }
}

.toggle-filter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
}

.more-filters-btn {
  position: relative;
}

.more-filters-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #f56c6c;
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
}

.state-block {
  padding: 24px 0;
}

.empty-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: -12px;
}

.table {
  width: 100%;
}

.mono {
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 12px;
}

.user-link {
  color: #007791;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.user-dialog__title {
  font-family: "Montserrat-Bold", sans-serif;
  font-size: 18px;
  color: #2a6df5;
}

.user-dialog__state {
  padding: 8px 0;
}

.user-hero {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 18px;
  background: linear-gradient(135deg, rgba(0, 119, 145, 0.08), rgba(66, 78, 121, 0.08));
  border: 1px solid rgba(66, 78, 121, 0.1);
  border-radius: 12px;

  &__avatar {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #007791;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: "Montserrat-Bold", sans-serif;
    font-size: 20px;
  }

  &__name {
    font-family: "Montserrat-Bold", sans-serif;
    font-size: 18px;
    color: #0a194e;
    margin-bottom: 6px;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
}

.user-section {
  margin-bottom: 18px;

  &:last-child {
    margin-bottom: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #424e79;
    margin-bottom: 10px;
  }
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  margin: 0;
  background: rgba(66, 78, 121, 0.08);
  border: 1px solid rgba(66, 78, 121, 0.08);
  border-radius: 8px;
  overflow: hidden;

  &__item {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 10px 14px;
    background: #fff;

    &--full {
      grid-column: 1 / -1;
    }

    dt {
      font-size: 13px;
      color: #9497a3;
    }

    dd {
      margin: 0;
      font-size: 15px;
      color: #2b2f38;
      word-break: break-word;
    }
  }
}

.phone-chip {
  margin: 0 6px 0 0;
}

.dim {
  margin-left: 6px;
  font-size: 13px;
  color: #b0b3bd;
}

.id-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.copy-btn {
  padding: 2px;
  height: auto;
  min-height: auto;
}

.route-hint {
  margin-top: 2px;
  font-size: 11px;
  color: #999;
}

.latency--slow {
  color: #e6a23c;
  font-weight: 600;
}

.pagination {
  margin-top: 14px;
  justify-content: flex-end;
}

.detail-panel {
  padding: 12px 16px;
  background: #fafbfd;

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 12px;
    font-size: 12px;
    color: #555;
  }

  &__bodies {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
  }

  &__body {
    h5 {
      margin: 0 0 6px;
      font-size: 12px;
      color: #424e79;
    }

    pre {
      margin: 0;
      padding: 10px;
      background: #fff;
      border: 1px solid rgba(66, 78, 121, 0.1);
      border-radius: 6px;
      font-size: 11px;
      white-space: pre-wrap;
      word-break: break-word;
      max-height: 240px;
      overflow: auto;
    }
  }

  &__empty {
    margin: 0;
    font-size: 12px;
    color: #999;
  }
}
</style>
